import TR from 'utils/TR';
import T from 'utils/T';
import ex from 'utils/ex';
import VBox from 'components/common/VBox';
import HBox from 'components/common/HBox';
import FocusableButton from 'components/common/FocusableButton';
import SimulationRadioButton from './SimulationRadioButton';
import Check from 'components/common/Check';
import { numberWithComma } from 'utils/number';
import React from 'react';
import WEEK_DAY_COLUMNS from 'constants/WEEK_DAY_COLUMNS';

const all = Array.from({ length: 21 }).map((_, index) => {
  const time_zone_tp_cd = Math.floor(index / 7) + 2;
  const weekday_tp_cd = index % 7 + 1;
  return `${time_zone_tp_cd}:${weekday_tp_cd}`;
});
export default function SimulationConditions(props) {
  const {
    disabled,
    selected,
    onSelect,
    data,
    steps,
    onSteps,
    onExecute,
    ...rest
  } = props;

  const {
    avg_bld_sugr_list = [],
    recommend_step_cnt = 2000,
    recommend_active_time_list = [],
  } = data;

  const isAll = React.useMemo(() => Object.keys(steps).filter(key => steps[key]).length === 21, [steps]);
  const isRecommended = React.useMemo(() => recommend_step_cnt === selected && !recommend_active_time_list.reduce((a, item) => {
    const {
      time_zone_tp_cd,
      weekday_tp_cd,
    } = item;
    const id = `${time_zone_tp_cd}:${weekday_tp_cd}`;
    return !steps[id] && a;
  }, true), [recommend_active_time_list, recommend_step_cnt, selected, steps]);
  const recommended = React.useMemo(() => recommend_active_time_list.map(item => {
    const {
      time_zone_tp_cd,
      weekday_tp_cd,
    } = item;
    const id = `${time_zone_tp_cd}:${weekday_tp_cd}`;
    return id;
  }), [recommend_active_time_list]);
  const onRecommended = React.useCallback((callbacks) => () => callbacks.forEach(callback => callback && callback()), []);

  const hash = React.useMemo(() => {
    const hs = {
      0: [],
      1: [],
      2: [],
    };
    avg_bld_sugr_list?.forEach(item => {
      const {
        avg_bld_sugr_fri,
        avg_bld_sugr_mon,
        avg_bld_sugr_sat,
        avg_bld_sugr_sun,
        avg_bld_sugr_thu,
        avg_bld_sugr_tue,
        avg_bld_sugr_wed,
        time_zone_tp_cd,
      } = item;
      hs[time_zone_tp_cd - 2] = [
        avg_bld_sugr_mon,
        avg_bld_sugr_tue,
        avg_bld_sugr_wed,
        avg_bld_sugr_thu,
        avg_bld_sugr_fri,
        avg_bld_sugr_sat,
        avg_bld_sugr_sun,
      ];
    });
    return hs;
  }, [avg_bld_sugr_list]);

  return (
    <HBox {...ex('SimulationConditions', rest)}>
      <VBox>
        <SimulationRadioButton {...{ selected: selected === 2000, onClick: onSelect(2000) }}>
          <div>
            {T('15분, 120kcal')}
          </div>
          <div>
            {T('2,000보 활동 추가 시')}
          </div>
          {recommend_step_cnt === 2000 && (
            <HBox {...ex('AI')}>
              <div>
                {T('AI')}
              </div>
            </HBox>
          )}
        </SimulationRadioButton>
        <SimulationRadioButton {...{ selected: selected === 1000, onClick: onSelect(1000) }}>
          <div>
            {T('15분, 60kcal')}
          </div>
          <div>
            {T('1,000보 활동 추가 시')}
          </div>
          {recommend_step_cnt === 1000 && (
            <HBox {...ex('AI')}>
              <div>
                {T('AI')}
              </div>
            </HBox>
          )}
        </SimulationRadioButton>
      </VBox>
      <VBox>
        <HBox>
          <Check
            {...{
              checked: isAll,
              onClick: onSteps(all, !isAll),
            }}
          >
            {T('전체 선택')}
          </Check>
          <Check
            {...{
              checked: isRecommended,
              onClick: onRecommended([onSelect(recommend_step_cnt), onSteps(recommended, !isRecommended)]),
            }}>
            {T('AI 추천만 선택')}
          </Check>
        </HBox>
        <table>
          <thead>
            <tr>
              <th>
                <div></div>
              </th>
              {WEEK_DAY_COLUMNS.map(text => (
                <th key={text}>
                  <div>{text}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <div>
                  {T('오전')}
                </div>
              </td>
              {hash[0].map((value, index) => {
                const id = `${2}:${index + 1}`;
                const selected = steps[id];
                const isAI = recommended.includes(id);
                return (
                  <td key={index}>
                    <HBox>
                      <FocusableButton
                        {...ex({ selected })}
                        {...{
                          onClick: onSteps([id]),
                        }}
                      >
                        <VBox  {...ex('Value')}>
                          <div>
                            {value}
                          </div>
                        </VBox>
                        {isAI && (
                          <HBox {...ex('AI')}>
                            <div>
                              {T('AI')}
                            </div>
                          </HBox>
                        )}
                      </FocusableButton>
                    </HBox>
                  </td>
                )
              })}
            </tr>
            <tr>
              <td>
                <div>
                  {T('오후')}
                </div>
              </td>
              {hash[1].map((value, index) => {
                const id = `${3}:${index + 1}`;
                const selected = steps[id];
                const isAI = recommended.includes(id);
                return (
                  <td key={index}>
                    <HBox>
                      <FocusableButton
                        {...ex({ selected })}
                        {...{
                          onClick: onSteps([id]),
                        }}
                      >
                        <VBox  {...ex('Value')}>
                          <div>
                            {value}
                          </div>
                        </VBox>
                        {isAI && (
                          <HBox {...ex('AI')}>
                            <div>
                              {T('AI')}
                            </div>
                          </HBox>
                        )}
                      </FocusableButton>
                    </HBox>
                  </td>
                )
              })}
            </tr>
            <tr>
              <td>
                <div>
                  {T('저녁')}
                </div>
              </td>
              {hash[2].map((value, index) => {
                const id = `${4}:${index + 1}`;
                const selected = steps[id];
                const isAI = recommended.includes(id);
                return (
                  <td key={index}>
                    <HBox>
                      <FocusableButton
                        {...ex({ selected })}
                        {...{
                          onClick: onSteps([id]),
                        }}
                      >
                        <VBox  {...ex('Value')}>
                          <div>
                            {value}
                          </div>
                        </VBox>
                        {isAI && (
                          <HBox {...ex('AI')}>
                            <div>
                              {T('AI')}
                            </div>
                          </HBox>
                        )}
                      </FocusableButton>
                    </HBox>
                  </td>
                )
              })}
            </tr>
          </tbody>
        </table>
        <HBox>
          <FocusableButton
            {...ex(['ExecuteButton', 'primary'])}
            {...{
              disabled,
              onClick: onExecute,
            }}
          >
            {T('실행하기')}
          </FocusableButton>
        </HBox>
      </VBox>

    </HBox>
  );
}
